import React from 'react';
import { useIntl } from 'react-intl';
import cx from 'classnames';

import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { isServer } from '@xingternal/hops';
import {
  trackContactOpenMaps,
  trackContactClickPhoneNumber,
  trackContactClickEmail,
  trackContactClickWebpage,
} from '@xingternal/pages-tracking';

import { useModuleContext } from '@xingternal/pages-common';

import { BodyCopy } from '@xingternal/typography';
import { Card } from '@xingternal/card';
import { Button, TextButton } from '@xingternal/button';
import Map from './Map';
import { LocationsQuery } from './LocationsQuery.gql-types';

import styles from './Location.css';

type LocationProps = NonNullable<
  LocationsQuery['entityPageLocations']
>['collection'][0] & {
  trackingModuleType: string;
};

const buildContactNumber = (countryCode: number, number: string) =>
  parsePhoneNumberFromString(
    `+${countryCode}${number}`
  )?.formatInternational() || '';

const Location = ({
  trackingModuleType,
  label,
  city,
  address,
  postcode,
  country,
  coordinates,
  email,
  phoneNumber,
  faxNumber,
  websiteURL,
}: LocationProps) => {
  const intl = useIntl();
  const { moduleContext } = useModuleContext();
  return (
    <div className={styles.mapsWrapper}>
      {!isServer && moduleContext.intersected && coordinates && (
        <Map lat={coordinates?.latitude} lng={coordinates?.longitude} />
      )}
      <Button
        variant={'secondary'}
        size={'small'}
        className={styles.mapsButton}
        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
          `${address}, ${postcode} ${city}, ${country?.localizationValue}`
        )}`}
        onClick={() => trackContactOpenMaps()}
        target="_blank"
        data-cy="open_maps_btn"
        data-testid="locations-map-external"
        rel="nofollow"
      >
        {intl.formatMessage({
          id: 'EP_LOCATIONS_OPEN_MAP',
          defaultMessage: 'EP_LOCATIONS_OPEN_MAP',
        })}
      </Button>

      <Card
        className={styles.addressWrapper}
        data-testid="locations-address-card"
      >
        <BodyCopy
          fontWeight="bold"
          lineHeight="dense"
          size={'xlarge'}
          noMargin
          className={styles.mapHeadline}
        >
          {label}
        </BodyCopy>
        <BodyCopy size={'small'} className={styles.address}>
          {`${address}, ${postcode} ${city}, ${
            country &&
            intl.formatMessage({
              id: country.localizationKey,
              defaultMessage: country.localizationKey,
            })
          }`}{' '}
        </BodyCopy>
        <div className={styles.allContactsWrapper}>
          <div className={styles.contactNumbers}>
            {phoneNumber && (
              <div className={styles.linkableContactWrapper}>
                <Button
                  fontWeight="regular"
                  lineHeight="dense"
                  className={styles.phone}
                  size={'small'}
                  noMargin
                  onClick={() =>
                    trackContactClickPhoneNumber(trackingModuleType)
                  }
                  href={`tel:+${phoneNumber.countryCode}${phoneNumber.number}`}
                >
                  {`${intl.formatMessage({
                    id: 'EP_TELEPHONE',
                    defaultMessage: 'EP_TELEPHONE',
                  })}: ${buildContactNumber(
                    phoneNumber.countryCode,
                    phoneNumber.number
                  )}`}
                </Button>
              </div>
            )}
            {faxNumber && (
              <BodyCopy noMargin size={'small'}>
                {`${intl.formatMessage({
                  id: 'EP_FAX',
                  defaultMessage: 'EP_FAX',
                })}: ${buildContactNumber(
                  faxNumber.countryCode,
                  faxNumber.number
                )}`}
              </BodyCopy>
            )}
          </div>
          {email && (
            <div className={styles.linkableContactWrapper}>
              <TextButton
                size={'small'}
                className={styles.webContacts}
                href={`mailto:${email}`}
                onClick={() => trackContactClickEmail(trackingModuleType)}
              >
                {email}
              </TextButton>
            </div>
          )}
          {websiteURL && (
            <div
              className={cx(styles.linkableContactWrapper, styles.websiteLink)}
            >
              <TextButton
                size={'small'}
                className={styles.webContacts}
                target={'_blank'}
                href={websiteURL}
                onClick={() => trackContactClickWebpage(trackingModuleType)}
                rel={'nofollow'}
              >
                {websiteURL}
              </TextButton>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default Location;
